import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API, useAuth } from "../App";
import { Heart, Home, Stethoscope, HandHeart, TrendingUp, Users, Sparkles, ShieldCheck, ArrowRight, Wallet, UserCircle, Book, Gift } from "lucide-react";
import QuickActions from "../components/QuickActions";

const NGO_QUICK_ACTIONS = [
  { to: "/app/ngo/rescue-cases",       icon: Heart,         label: "Rescue cases",        desc: "Active cases · update status", color: "bg-brand text-white" },
  { to: "/app/ngo/foster-applications", icon: Users,         label: "Foster apps",         desc: "Review · approve",            color: "bg-leaf/30 text-ink" },
  { to: "/app/ngo/donations",          icon: Gift,           label: "Donations",           desc: "Donor list · 80G receipts",   color: "bg-sun/40 text-ink" },
  { to: "/app/adoption",               icon: HandHeart,      label: "Adoption listings",    desc: "Post · publish · adopt out", color: "bg-brand-green text-white" },
  { to: "/app/ngo/register",           icon: Book,           label: "Animal register",     desc: "ABC 2023 court-ready PDF",    color: "bg-brand-blue text-white" },
  { to: "/app/payout-setup",           icon: Wallet,         label: "Payouts setup",       desc: "Bank · UPI · auto-settle",    color: "bg-sky/15 text-sky" },
  { to: "/app/profile",                icon: UserCircle,     label: "Edit profile",        desc: "Name · city · photo · 80G",   color: "bg-ink/10 text-ink" },
];

export default function NgoImpact() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [compliance, setCompliance] = useState(null);
  useEffect(() => {
    axios.get(`${API}/partner/ngo/impact`).then(r => setData(r.data)).catch(() => setData({ error: true }));
    axios.get(`${API}/ngo/register/compliance-summary`).then(r => setCompliance(r.data)).catch(() => {});
  }, []);

  if (user?.role !== "ngo") return (
    <div className="bg-surface border border-line rounded-3xl p-8 text-center">
      <h2 className="font-heading text-2xl font-extrabold">NGO account required</h2>
    </div>
  );
  if (!data) return <div className="text-ink-soft">Loading impact…</div>;
  if (data.error) return <div className="text-ink-soft">Could not load impact dashboard.</div>;
  const k = data.kpis || {};
  return (
    <div className="space-y-6" data-testid="ngo-impact-page">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">My NGO · Impact</div>
        <h1 className="font-heading text-3xl font-extrabold mt-1">{data.profile?.name}</h1>
        <p className="text-ink-soft text-sm">Numbers that matter — share this page with donors & investors.</p>
      </header>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI icon={Heart} accent="bg-brand text-white" label="Lives saved" value={k.lives_saved}/>
        <KPI icon={Home} accent="bg-leaf text-ink" label="In foster" value={k.in_foster}/>
        <KPI icon={Stethoscope} accent="bg-brand-blue text-white" label="Under treatment" value={k.active_treatment}/>
        <KPI icon={Sparkles} accent="bg-brand-green text-white" label="Adopted" value={k.adopted}/>
      </div>

      <QuickActions items={NGO_QUICK_ACTIONS} testid="ngo-quick-actions"/>

      <div className="grid md:grid-cols-3 gap-4">
        <BigStat label="Total donations" value={`₹${(k.donations_total || 0).toLocaleString()}`} icon={HandHeart}/>
        <BigStat label="Sponsorship pledged" value={`₹${(k.sponsorship_total || 0).toLocaleString()}`} icon={TrendingUp} sub={`${k.active_sponsors || 0} active sponsors · ₹${(k.monthly_recurring || 0).toLocaleString()}/mo`}/>
        <BigStat label="Foster applications" value={k.foster_applications} icon={Users} sub={`${k.foster_approved || 0} approved`}/>
      </div>
      {compliance && (
        <Link to="/app/ngo/register" data-testid="compliance-card" className="block bg-surface border border-line rounded-3xl p-5 hover:-translate-y-0.5 hover:shadow-soft transition-all">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-2xl grid place-items-center ${compliance.avg_compliance_pct >= 90 ? "bg-brand-green text-white" : "bg-sun text-ink"}`}>
                <ShieldCheck className="w-6 h-6"/>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.2em] text-brand font-bold">ABC Rules 2023 · Court-Mandated</div>
                <h2 className="font-heading text-xl font-extrabold">Animal Register · {compliance.avg_compliance_pct}% compliant</h2>
                <div className="text-xs text-ink-soft mt-0.5">{compliance.totals.animals} animals · {compliance.totals.sterilized} sterilized · {compliance.totals.anti_rabies} ARV · {compliance.totals.court_ready} court-ready</div>
              </div>
            </div>
            <div className="text-sm font-bold text-brand inline-flex items-center gap-1">Open register <ArrowRight className="w-4 h-4"/></div>
          </div>
        </Link>
      )}
      <section className="bg-surface border border-line rounded-3xl p-5">
        <div className="flex items-center justify-between">
          <h2 className="font-heading text-xl font-extrabold">Recent rescues</h2>
          <Link to="/app/ngo/rescue-cases" className="text-sm font-bold text-brand">Manage all →</Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
          {(data.recent_cases || []).map(c => (
            <div key={c.rescue_case_id} className="bg-surface-muted/50 rounded-2xl p-3 flex gap-3">
              <img src={c.photo} alt={c.pet_name} className="w-16 h-16 rounded-xl object-cover"/>
              <div>
                <div className="font-heading font-extrabold">{c.pet_name}</div>
                <div className="text-xs text-ink-soft uppercase tracking-wider">{c.status}</div>
                <div className="text-xs text-ink-soft mt-0.5 line-clamp-1">{c.intake_location}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent }) {
  return (
    <div className="bg-surface border border-line rounded-3xl p-5">
      <div className={`w-9 h-9 rounded-xl grid place-items-center ${accent}`}><Icon className="w-5 h-5"/></div>
      <div className="font-heading text-3xl font-extrabold mt-3">{value ?? 0}</div>
      <div className="text-xs uppercase tracking-wider text-ink-soft font-bold mt-0.5">{label}</div>
    </div>
  );
}

function BigStat({ icon: Icon, label, value, sub }) {
  return (
    <div className="bg-ink text-bg rounded-3xl p-5">
      <div className="flex items-center justify-between">
        <div className="text-xs uppercase tracking-wider font-bold opacity-70">{label}</div>
        <Icon className="w-5 h-5 opacity-70"/>
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value}</div>
      {sub && <div className="text-xs opacity-70 mt-1">{sub}</div>}
    </div>
  );
}
